import { useCallback, useEffect } from "react";
import { readConnectorSetupGuide, type ConnectorSetupGuide } from "../../../api/connectorSetupApi";
import { listConnectors, readConnectorServerAddress, type ListedConnector } from "../../../api/connectorsApi";
import { useSession } from "../../../session/sessionContext";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";
import { setupProgressOf, type SetupProgress } from "./setupProgress";

/** Every 3 seconds while the guide waits for the PC (pairing, first report), 15 otherwise (OB-2). */
export const WAITING_POLL_MILLISECONDS = 3_000;
const IDLE_POLL_MILLISECONDS = 15_000;

export interface ConnectorSetupView {
  readonly guide: ConnectorSetupGuide;
  readonly progress: SetupProgress;
  readonly connectors: readonly ListedConnector[];
}

/** Read the guide's stored state and the facts its progress is derived from, in one load. */
export function loadConnectorSetup(isAdministrator: boolean, signal?: AbortSignal): Promise<ConnectorSetupView> {
  return Promise.all([readConnectorSetupGuide(signal), listConnectors(signal), readConnectorServerAddress(signal)]).then(
    ([guide, connectors, address]) => ({
      guide,
      connectors,
      progress: setupProgressOf({ isAdministrator, serverAddressConfigured: address !== null, connectors }),
    }),
  );
}

/**
 * The guide's state and progress for a signed-in view, asked again while the guide is active
 * and the tab is visible: often while it waits on the PC, now and then otherwise. A guide put
 * away or not met yet is read once, so the account menu's glow costs nothing then.
 */
export function useConnectorSetup(): LoadHandle<ConnectorSetupView> {
  const { session } = useSession();
  const isAdministrator = session.status === "signedIn" && session.session.account.accountRole === "administrator";
  const load = useCallback((signal: AbortSignal) => loadConnectorSetup(isAdministrator, signal), [isAdministrator]);
  const handle = useLoad(load);
  const phase = handle.state.status === "loaded" ? handle.state.value.progress.phase : null;
  const active = handle.state.status === "loaded" && handle.state.value.guide.state === "active";
  const waiting = phase === "needs_pairing" || phase === "needs_first_report";
  useRefreshWhile(active, waiting ? WAITING_POLL_MILLISECONDS : IDLE_POLL_MILLISECONDS, handle.refresh);
  return handle;
}

/**
 * Asks again every `milliseconds` while `active` and the tab is visible: the setup guide while
 * it is active, and the Connectors page while it waits for the first PC, which pairs from the
 * connector app without another click here (F122).
 */
export function useRefreshWhile(active: boolean, milliseconds: number, refresh: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, milliseconds);
    return () => {
      window.clearInterval(timer);
    };
  }, [active, milliseconds, refresh]);
}

/**
 * Whether the connector entry points glow (F32): while the guide is active and no connector of
 * the person's own PC is connected, so they learn where it lives. The guide's button and the
 * connect button use it; so may the account menu's Connectors entry.
 */
export function useConnectorSetupGlow(): boolean {
  const { state } = useConnectorSetup();
  return state.status === "loaded" && glowsFor(state.value);
}

/**
 * The navigation state of the buttons that start the setup (F46): the guide opens at the
 * current step, without the welcome, and resumes if it was put away. "Connect another PC"
 * opens it at pairing, whatever the current step (F78).
 */
export const GUIDE_STARTS_AT_ONCE = { connectorGuide: "start" } as const;
export const GUIDE_STARTS_AT_PAIRING = { connectorGuide: "pair" } as const;

/** Where a button asked the guide to start: at the current step, at pairing, or nowhere. */
export function guideStartOf(navigationState: unknown): "current" | "pair" | null {
  const asked = typeof navigationState === "object" && navigationState !== null ? (navigationState as { connectorGuide?: unknown }).connectorGuide : undefined;
  return asked === "start" ? "current" : asked === "pair" ? "pair" : null;
}

export function glowsFor(view: Pick<ConnectorSetupView, "guide" | "progress">): boolean {
  return view.guide.state === "active" && !view.progress.pcConnectorOnline;
}
